import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faList,
    faTimeline
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { capitalizeFirstLetter } from '../../utils/formatText';
import { formatDayLabel } from '../../utils/formatDate';

export default function ProgramHeader({
    // state
    selectedCategory,
    selectedDayLabel,
    selectedDate,
    selectedDateIndex,
    viewMode,
    isDayPickerOpen,
    isDayDropdownOpen,

    // derived
    uniqueDates,
    categoryAvailability,

    // refs
    dayLabelBtnRef,
    dropdownRef,

    // handlers
    setSelectedCategory,
    setSelectedDate,
    setIsDayDropdownOpen,
    openDayPicker,
    goPrevDay,
    goNextDay,
    setViewModeAndPersist
}) {
    const renderCategoryButtons = () =>
        ['trening', 'konferanse'].map(catKey => {
            const enabled =
                catKey === 'trening'
                    ? categoryAvailability.treningEnabled
                    : categoryAvailability.konferanseEnabled;

            const label = capitalizeFirstLetter(catKey);

            return (
                <button
                    key={catKey}
                    className={`program__seg-btn ${catKey} ${catKey === selectedCategory ? 'is-active' : ''}`}
                    onClick={() => enabled && setSelectedCategory(catKey)}
                    disabled={!enabled}
                    aria-disabled={!enabled}
                    role="tab"
                    aria-selected={catKey === selectedCategory}
                    type="button"
                >
                    {label}
                </button>
            );
        });

    const renderViewButtons = () => (
        <>
            <button
                type="button"
                className={`program__icon-toggle ${viewMode === 'list' ? 'is-active' : ''}`}
                onClick={() => setViewModeAndPersist('list')}
                role="tab"
                aria-selected={viewMode === 'list'}
                aria-label="Listevisning"
            >
                <FontAwesomeIcon icon={faList} />
            </button>

            <button
                type="button"
                className={`program__icon-toggle ${viewMode === 'timeline' ? 'is-active' : ''}`}
                onClick={() => setViewModeAndPersist('timeline')}
                role="tab"
                aria-selected={viewMode === 'timeline'}
                aria-label="Tidslinje"
            >
                <FontAwesomeIcon icon={faTimeline} />
            </button>
        </>
    );

    return (
        <div className="program__header">
            {/* Desktop: 1 rad (kategori | daypager | visning) */}
            <div className="program__header-row program__header-row--desktop">
                <div className="program__header-left">
                    <div className="program__segmented" role="tablist" aria-label="Velg kategori">
                        {renderCategoryButtons()}
                    </div>
                </div>

                <div className="program__header-center">
                    <div className="program__daypager" aria-label="Velg dag">
                        <button
                            type="button"
                            className="program__icon-btn"
                            onClick={goPrevDay}
                            disabled={selectedDateIndex <= 0}
                            aria-label="Forrige dag"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>

                        <button
                            type="button"
                            className="program__daylabel"
                            onClick={openDayPicker}
                            aria-haspopup="listbox"
                            aria-expanded={isDayPickerOpen || isDayDropdownOpen}
                            ref={dayLabelBtnRef}
                        >
                            <span className="program__daylabel-text">{selectedDayLabel}</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="program__daylabel-caret"
                            />
                        </button>

                        <button
                            type="button"
                            className="program__icon-btn"
                            onClick={goNextDay}
                            disabled={
                                selectedDateIndex < 0 || selectedDateIndex >= uniqueDates.length - 1
                            }
                            aria-label="Neste dag"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>

                        {/* Desktop dropdown */}
                        {isDayDropdownOpen && (
                            <div className="program__daydropdown" ref={dropdownRef} role="listbox">
                                {uniqueDates.map(dk => {
                                    const isActive = dk === selectedDate;

                                    return (
                                        <button
                                            key={dk}
                                            type="button"
                                            className={`program__daydropdown-item ${isActive ? 'is-active' : ''}`}
                                            onClick={() => {
                                                setSelectedDate(dk);
                                                setIsDayDropdownOpen(false);
                                            }}
                                            role="option"
                                            aria-selected={isActive}
                                        >
                                            {formatDayLabel(dk)}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="program__header-right">
                    <div className="program__view" role="tablist" aria-label="Velg visning">
                        {renderViewButtons()}
                    </div>
                </div>
            </div>

            {/* Mobil/tablet: 2 rader */}
            <div className="program__header-row program__header-row--mobile">
                <div className="program__header-mobile-top">
                    <div className="program__daypager" aria-label="Velg dag">
                        <button
                            type="button"
                            className="program__icon-btn"
                            onClick={goPrevDay}
                            disabled={selectedDateIndex <= 0}
                            aria-label="Forrige dag"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>

                        <button
                            type="button"
                            className="program__daylabel"
                            onClick={openDayPicker}
                            aria-haspopup="dialog"
                            aria-expanded={isDayPickerOpen}
                        >
                            <span className="program__daylabel-text">{selectedDayLabel}</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="program__daylabel-caret"
                            />
                        </button>

                        <button
                            type="button"
                            className="program__icon-btn"
                            onClick={goNextDay}
                            disabled={
                                selectedDateIndex < 0 || selectedDateIndex >= uniqueDates.length - 1
                            }
                            aria-label="Neste dag"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>

                <div className="program__header-mobile-bottom">
                    <div className="program__segmented" role="tablist" aria-label="Velg kategori">
                        {renderCategoryButtons()}
                    </div>

                    <div className="program__view" role="tablist" aria-label="Velg visning">
                        {renderViewButtons()}
                    </div>
                </div>
            </div>
        </div>
    );
}
